import {Button, Chip, Grid, useTheme} from "@mui/material";
import React, {Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {useAgGridLocale} from "../../../../../config/globalConfig";
import {
    CellClassParams,
    ColDef,
    ICellRendererParams,
    IHeaderGroupParams,
    ITooltipParams,
    ProcessCellForExportParams
} from "ag-grid-community";
import {
    changeCarGroups,
    changeRentalPeriods,
    fromGenericBeToGenericFe,
    heightDynamicMatrix,
    i18nConvertNumberWithDecimals
} from "../../../utils";
import {DEFAULT_MATRIX_COL_DEF, maxNumberYupLimit, SET_SNACKBAR} from "../../../constants";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {useTranslation} from "react-i18next";
import {GridApi} from "ag-grid-enterprise";
import DialogComponent from "../../../common/components/DialogComponent";
import MatrixInitialization from "./MatrixInitialization";
import {
    AncillaryDetail,
    MatrixDictionary,
    MatrixElementInterface,
    MatrixInitializationValues
} from "../../../api/types/ancillaryDetail";
import {useAncillaryDetailRepository} from "../../../api/repositories/AncillaryDetailRepository";
import {useRentalPeriodRepository} from "../../../api/repositories/RentalPeriodRepository";
import {initialValues, validationSchema} from "./FormMatrixInitialization";
import {useNavigate} from "react-router-dom";
import {DialogInterface, SelectElementModelBE, SelectElementModelFE} from "../../../api/types/generic";
import {RentalPeriodEntityResponse} from "../../../api/types/rentalPeriods";
import {
    AncillaryEntityRequest,
    AncillaryEntityResponse,
    FormAncillaryModel,
    TaxCodeObject
} from "../../../api/types/ancillary";
import CarGroupsAndRentalPeriods from "../../../common/components/CarGroupsAndRentalPeriods";
import {useConstantsRepository} from "../../../api/repositories/ConstantsRepository";
import {Sell} from "../../../api/types/constants";
import CustomBanner from "../../../common/components/CustomBanner";
import {FormProvider, useForm, useFormContext} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import {buildEntityRequestFromFormValues} from "../BaseDefinition/AncillaryBaseDefinitionForm";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {useRecoilState} from "recoil";
import {ancillaryAtom, ancillaryServiceItem, exceptionPackAtom} from "../../../state/ancillariesRecoil";
import NumberCustomEditorComponent from "../../../common/components/NumberCustomEditorComponent";
import PercentageCellEditor from "../../../common/components/PercentageCellEditor";
import {FormAutocomplete} from "../../../common/form-fields";
import dayjs from "dayjs";
import {useAcrissRepository} from "../../../api/repositories/AcrissRepository";
import {ExceptionListEntityResponse} from "../../../api/types/exceptionList";
import CheckboxRenderer from "../../Rate/Matrix/CheckboxRenderer";
import { convertToCents } from "../../../../../util/state/commonFunctions";

const AncillaryMatrix = ({
                             ancillaryId,
                             readOnly,
                             ancillaryInUse,
                             setValue
                         }: {
    ancillaryId: number | null
    readOnly: boolean
    ancillaryInUse: boolean
    setValue: Function
}): JSX.Element => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Theme
    const theme = useTheme();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Form Context
    const ancillaryForm = useFormContext<FormAncillaryModel>();

    // Use Recoil
    const [ancillary, setAncillary] = useRecoilState<AncillaryEntityResponse | null>(ancillaryAtom);
    const [exceptionPack] = useRecoilState<ExceptionListEntityResponse | null>(exceptionPackAtom)

    // Use State
    const [matrix, setMatrix] = useState<MatrixDictionary>({});
    const [initialMatrix, setInitialMatrix] = useState<MatrixDictionary>({});
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    // Use Navigate
    const navigate = useNavigate();

    const onGridReady = (params: any) => {
        setGridApi(params.api);
    };

    // Use Context
    const {
        globalData: {user},
    } = useContext(GlobalContext);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    // Use State
    const [ancillaryDetails, setAncillaryDetails] = useState<AncillaryDetail[]>([]);
    const [carGroupRows, setCarGroupRows] = useState<string[]>([]);
    const [allCarGroupRows, setAllCarGroupRows] = useState<string[]>([]);
    const [initialCarGroupRows, setInitialCarGroupRows] = useState<string[]>([]);
    const [rentalPeriodColumns, setRentalPeriodColumns] = useState<RentalPeriodEntityResponse[]>([]);
    const [allRentalPeriodColumns, setAllRentalPeriodColumns] = useState<RentalPeriodEntityResponse[]>([]);
    const [initialRentalPeriodColumns, setInitialRentalPeriodColumns] = useState<RentalPeriodEntityResponse[]>([]);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [sellList, setSellList] = useState<SelectElementModelFE[]>([]);
    const [serviceItem] = useRecoilState<any>(ancillaryServiceItem);

    // Use Context
    const {dispatchGlobalData} = useContext(GlobalContext);

    // Use Repository Hooks
    const {
        getAncillaryDetails,
        getAncillaryDetailsFromCopyAncillary,
        persistAncillaryAndDetails
    } = useAncillaryDetailRepository();
    const {getRentalPeriods, getRentalPeriodsSorted} = useRentalPeriodRepository();
    const {getSell} = useConstantsRepository();
    const {getOrderedAcriss, orderCarGroupsByAcrissOrder} = useAcrissRepository();

    const daysOfTheWeekUppercase = [
        {code: 1, label: dayjs().day(1).format("dddd").toUpperCase()},
        {code: 2, label: dayjs().day(2).format("dddd").toUpperCase()},
        {code: 3, label: dayjs().day(3).format("dddd").toUpperCase()},
        {code: 4, label: dayjs().day(4).format("dddd").toUpperCase()},
        {code: 5, label: dayjs().day(5).format("dddd").toUpperCase()},
        {code: 6, label: dayjs().day(6).format("dddd").toUpperCase()},
        {code: 7, label: dayjs().day(0).format("dddd").toUpperCase()}
    ];

    // Use Effect
    useEffect(() => {
        const getMatrixInfo = async () => {
            // UPDATE ACCESSORIO
            if (ancillaryId && ancillary) {
                try {
                    const ancillaryDetails: AncillaryDetail[] = (await getAncillaryDetails(ancillaryId))
                        .map((ancillaryDetail: AncillaryDetail) => {
                            return {
                                ...ancillaryDetail,
                                price: i18nConvertNumberWithDecimals(Number(ancillaryDetail.price) / 100)
                            };
                        });
                    setAncillaryDetails(ancillaryDetails);

                    const sellList = await getSell();
                    setSellList(sellList.map((sell: Sell) => {
                        return {
                            code: sell.sellType,
                            label: sell.description
                        };
                    }));

                    let rentalPeriods: RentalPeriodEntityResponse[];
                    let carGroups: string[];
                    let allRentalPeriods: RentalPeriodEntityResponse[];
                    let allCarGroups: string[];

                    // ancillary con matrice già esistente
                    if (ancillaryDetails.length > 0) {
                        const carGroupsAndRentalPeriods = populateCarGroupsAndRentalPeriods({ancillaryDetails});
                        rentalPeriods = carGroupsAndRentalPeriods.rentalPeriods;
                        carGroups = carGroupsAndRentalPeriods.carGroups;
                        allRentalPeriods = (await getRentalPeriods()).filter((rp: RentalPeriodEntityResponse) => {
                            return rp.tpUnit === ancillary.tpUnit;
                        }).sort((a, b) => a.from - b.from);
                        allRentalPeriods = await getRentalPeriodsSorted(allRentalPeriods)

                        // TODO: togliere CAR_GROUPS || quando si pusha
                        // allCarGroups = CAR_GROUPS.map((c: CarGroup) => c.code) || (await getOrderedAcriss()).map(el => el.acriss);
                        allCarGroups = (await getOrderedAcriss(ancillary.legalEntity as string)).map(el => el.acriss);
                        allCarGroups = await orderCarGroupsByAcrissOrder(allCarGroups, ancillary.legalEntity as string)
                    } else {
                        rentalPeriods = allRentalPeriods = (await getRentalPeriods()).filter((rp: RentalPeriodEntityResponse) => {
                            return rp.tpUnit === ancillary.tpUnit;
                        }).sort((a, b) => a.from - b.from);
                        rentalPeriods = await getRentalPeriodsSorted(rentalPeriods)

                        // togliere CAR_GROUPS || quando si pusha
                        // carGroups = allCarGroups = CAR_GROUPS.map((c: CarGroup) => c.code) || (await getOrderedAcriss()).map(el => el.acriss);
                        carGroups = allCarGroups = (await getOrderedAcriss(ancillary.legalEntity as string)).map(el => el.acriss);
                        carGroups = await orderCarGroupsByAcrissOrder(carGroups, ancillary.legalEntity as string)
                    }

                    setCarGroupsAndRentalPeriodsAndMatrix({
                        rentalPeriods,
                        carGroups,
                        allRentalPeriods,
                        allCarGroups,
                        ancillary,
                        ancillaryDetails,
                        setRentalPeriodColumns,
                        setAllRentalPeriodColumns,
                        setInitialRentalPeriodColumns,
                        setCarGroupRows,
                        setAllCarGroupRows,
                        setInitialCarGroupRows,
                        setMatrix,
                        setInitialMatrix,
                        buildMatrix
                    });
                    setLoading(false);
                } catch (err) {
                    setLoading(false);
                }
            }
            // CREAZIONE ACCESSORIO
            else {
                try {
                    const sellList = await getSell();
                    setSellList(sellList.map((sell: Sell) => {
                        return {
                            code: sell.sellType,
                            label: sell.description
                        };
                    }));

                    let rentalPeriods: RentalPeriodEntityResponse[];
                    let carGroups: string[];
                    let allRentalPeriods: RentalPeriodEntityResponse[];
                    let allCarGroups: string[];

                    rentalPeriods = allRentalPeriods = (await getRentalPeriods()).filter((rp: RentalPeriodEntityResponse) => {
                        return rp.tpUnit === ancillaryForm?.getValues().tpUnit;
                    }).sort((a, b) => a.from - b.from);

                    const ancillaryEntityRequest: AncillaryEntityRequest = buildEntityRequestFromFormValues(ancillaryForm.getValues(), everyLegalEntitiesWithoutAll, dealerFilteredOptions)
                    const updateAncillary: AncillaryEntityResponse = {id: Number(ancillaryForm?.getValues().id), ...ancillaryEntityRequest};

                    // togliere CAR_GROUPS || quando si pusha
                    // carGroups = allCarGroups = CAR_GROUPS.map((c: CarGroup) => c.code) || (await getOrderedAcriss()).map(el => el.acriss);
                    carGroups = allCarGroups = (await getOrderedAcriss(ancillaryEntityRequest.legalEntity)).map(el => el.acriss);

                    setAncillary(updateAncillary);

                    setCarGroupsAndRentalPeriodsAndMatrix({
                        rentalPeriods,
                        carGroups,
                        allRentalPeriods,
                        allCarGroups,
                        ancillary: updateAncillary,
                        ancillaryDetails,
                        setRentalPeriodColumns,
                        setAllRentalPeriodColumns,
                        setInitialRentalPeriodColumns,
                        setCarGroupRows,
                        setAllCarGroupRows,
                        setInitialCarGroupRows,
                        setMatrix,
                        setInitialMatrix,
                        buildMatrix
                    });
                    setLoading(false);
                } catch (err) {
                    setLoading(false);
                }
            }
        };

        getMatrixInfo();
        // eslint-disable-next-line
    }, []);

    const setCarGroupsAndRentalPeriodsAndMatrix = ({
                                                       rentalPeriods,
                                                       carGroups,
                                                       allRentalPeriods,
                                                       allCarGroups,
                                                       ancillary,
                                                       ancillaryDetails,
                                                       setRentalPeriodColumns,
                                                       setAllRentalPeriodColumns,
                                                       setInitialRentalPeriodColumns,
                                                       setCarGroupRows,
                                                       setAllCarGroupRows,
                                                       setInitialCarGroupRows,
                                                       setMatrix,
                                                       setInitialMatrix,
                                                       buildMatrix
                                                   }: {
        rentalPeriods: RentalPeriodEntityResponse[]
        carGroups: string[]
        allRentalPeriods: RentalPeriodEntityResponse[]
        allCarGroups: string[]
        ancillary: AncillaryEntityResponse
        ancillaryDetails: AncillaryDetail[]
        setRentalPeriodColumns: Dispatch<SetStateAction<RentalPeriodEntityResponse[]>>
        setAllRentalPeriodColumns: Dispatch<SetStateAction<RentalPeriodEntityResponse[]>>
        setInitialRentalPeriodColumns: Dispatch<SetStateAction<RentalPeriodEntityResponse[]>>
        setCarGroupRows: Dispatch<SetStateAction<string[]>>
        setAllCarGroupRows: Dispatch<SetStateAction<string[]>>
        setInitialCarGroupRows: Dispatch<SetStateAction<string[]>>
        setMatrix: Dispatch<SetStateAction<MatrixDictionary>>
        setInitialMatrix: Dispatch<SetStateAction<MatrixDictionary>>
        buildMatrix: Function
    }) => {
        setRentalPeriodColumns(rentalPeriods);
        setCarGroupRows(carGroups);
        matrixUpdateForm.reset({
            matrixInitializationType: 0,
            carGroups: carGroups,
            rentalPeriods: fromGenericBeToGenericFe(rentalPeriods),
            ancillary: null,
            discountIncreasePercentage: null,
            discountIncreaseFixed: null
        });

        setAllRentalPeriodColumns(JSON.parse(JSON.stringify(allRentalPeriods)));
        setAllCarGroupRows(JSON.parse(JSON.stringify(allCarGroups)));
        setInitialRentalPeriodColumns(JSON.parse(JSON.stringify(rentalPeriods)));
        setInitialCarGroupRows(JSON.parse(JSON.stringify(carGroups)));

        //costruire le righe della matrice a partire dal car group e mettendo in fila i periodi di noleggio
        if (ancillaryDetails.length > 0) {
            const accumulator: MatrixDictionary = buildMatrix(ancillaryDetails, ancillary, rentalPeriods, carGroups);

            setMatrix(accumulator);
            setInitialMatrix(JSON.parse(JSON.stringify(accumulator)));
        }
    };

    const populateCarGroupsAndRentalPeriods = ({ancillaryDetails}: { ancillaryDetails: AncillaryDetail[] }): { carGroups: string[], rentalPeriods: RentalPeriodEntityResponse[] } => {
        let rentalPeriods: RentalPeriodEntityResponse[] = [];
        let carGroups: string[] = [];

        ancillaryDetails.forEach(ancillaryDetail => {
            if (!rentalPeriods.find(rp => rp.id === ancillaryDetail.prcRentalPeriod.id)) {
                rentalPeriods.push(ancillaryDetail.prcRentalPeriod);
            }

            if (!carGroups.find(cg => cg === ancillaryDetail.carGroup)) {
                carGroups.push(ancillaryDetail.carGroup);
            }
        });
        return {
            carGroups,
            rentalPeriods
        };
    };

    const mapAncillaryDetails = ({ancillaryDetails}: { ancillaryDetails: AncillaryDetail[] }): AncillaryDetail[] => {
        return ancillaryDetails.map((ancillaryDetail: AncillaryDetail) => {
            return {
                ...ancillaryDetail,
                price: i18nConvertNumberWithDecimals(Number(ancillaryDetail.price) / 100),
                prcAncillary: ancillary?.id ? ancillaryDetail.prcAncillary : null
            };
        });
    };

    const cellValueSetter = (params: any) => {
        if (params?.newValue !== null && params?.newValue !== undefined) {
            params.newValue = (params.newValue + '').replace(/,/g, '.');
        }
        if (!Number(params.newValue) && params.newValue != 0) {
            return false;
        } else if (params.newValue <= maxNumberYupLimit && params.newValue >= 0) {
            params.data[params.colDef.field.split(".")[0]] = {
                ...params.data[params.colDef.field.split(".")[0]],
                price: params.newValue
            };
            return true;
        } else return false;
    };

    const cellValueSetterPrice = (params: any) => {
        console.log(params)
        if (params?.newValue === "Z") {
            params.data[params.colDef.field.split(".")[0]] = {
                ...params.data[params.colDef.field.split(".")[0]],
                sell: params.newValue,
                price: 0
            };
            return true;
        }
        if (params?.newValue !== null && params?.newValue !== undefined) {
            params.data[params.colDef.field.split(".")[0]] = {
                ...params.data[params.colDef.field.split(".")[0]],
                sell: params.newValue
            };
            return true;
        } else return false;
    };

    const buildMatrixColDef = ({
                                   rentalPeriodColumns,
                                   t,
                                   ancillary
                               }: {
        rentalPeriodColumns: RentalPeriodEntityResponse[]
        t: Function
        ancillary: AncillaryEntityResponse | null
    }): ColDef[] => {
        return [
            {
                headerName: t("pricing:ancillaryMatrix.labels.carGroup"),
                headerTooltip: t("pricing:ancillaryMatrix.labels.carGroup"),
                field: "carGroup",
                width: 120,
                editable: false,
                pinned: "left"
            },
            ...rentalPeriodColumns.map((rp: RentalPeriodEntityResponse) => ({
                //COSTRUZIONE HEADER COLONNE GESTENDO IL FATTO CHE POSSANO ESSERE ANCHE GIORNI DELLA SETTIMANA
                headerName: `${rp.name} 
                                (${rp.weekend ? daysOfTheWeekUppercase.find(d => d.code === rp.from)?.label : rp.from}
                               - ${rp.weekend ? daysOfTheWeekUppercase.find(d => d.code === rp.to)?.label : rp.to})`,
                headerTooltip: `${rp.name} 
                                   (${rp.weekend ? daysOfTheWeekUppercase.find(d => d.code === rp.from)?.label : rp.from}
                                  - ${rp.weekend ? daysOfTheWeekUppercase.find(d => d.code === rp.to)?.label : rp.to})`,
                headerClass: ["bordersRight"],
                cellStyle: {textAlign: "center", padding: 5},
                children: [
                    {
                        headerName: "",
                        field: rp.id + ".forfait",
                        headerClass: ["bordersRight"],
                        wrapText: true,
                        headerGroupComponent: (params: IHeaderGroupParams) => {
                            const currentDetail = Object.values(matrix)[0][rp.id];
                            return <CheckboxRenderer {...params}
                                                     width={100}
                                                     field={rp.id + ".forfait"}
                                                     disabled={readOnly || ancillary?.tpUnit === "RENTAL" || ancillary?.subtype === "DELIVERY" || ancillary?.subtype === "COLLECTION"}
                                                     defaultValue={currentDetail.forfait as boolean}
                                                     label={t("pricing:labels.forfait")}
                                                     onChange={(e: any) => {
                                                         const newValue = e.target.checked;
                                                         const newMatrix = {...matrix};
                                                         Object.values(newMatrix).forEach((value: any) => {
                                                             value[rp.id].forfait = newValue;
                                                         });
                                                         setMatrix(newMatrix);
                                                     }}
                            />
                        },
                        children: [{
                            headerName: rp.tpUnit,
                            headerTooltip: rp.tpUnit,
                            field: rp.id + ".price",
                            width: rentalPeriodColumns?.length > 6 ? 100 : null,
                            flex: rentalPeriodColumns?.length > 6 ? null : 1,
                            resizable: true,
                            cellEditor: "customEditor",
                            valueSetter: cellValueSetter,
                            cellRenderer: (params: ICellRendererParams) => {
                                return ancillary?.chargeType === "VALUE" ?
                                    i18nConvertNumberWithDecimals(params.value)
                                    :
                                    `${Number(params.value)}%`;
                            },
                            cellStyle: {
                                justifyContent: "flex-end",
                                textAlign: "right",
                                borderRight: "1px dotted #E0E0E0",
                                padding: 5
                            },
                            editable: !readOnly
                        },
                            {
                                headerName: `${t("pricing:ancillaryMatrix.labels.sellType")}*`,
                                headerTooltip: `${t("pricing:ancillaryMatrix.labels.sellType")}*`,
                                field: rp.id + ".sell",
                                tooltipField: rp.id + ".sell",
                                editable: !readOnly,
                                width: 200,
                                resizable: true,
                                cellEditor: "agRichSelectCellEditor",
                                cellEditorPopup: true,
                                cellEditorParams: {
                                    values: sellList.map(s => s.code),
                                    cellHeight: 20,
                                    width: 250,
                                    formatValue: (value: string) => value?.toUpperCase(),
                                    cellRenderer: (params: ICellRendererParams) =>
                                        <div>{params.value?.toUpperCase()}</div>
                                },
                                valueSetter: cellValueSetterPrice,
                                cellRenderer: (params: ICellRendererParams) =>
                                    getLabelChip(params.value?.toUpperCase()),
                                headerClass: ["bordersRight"],
                                cellStyle: (params: CellClassParams) => ({
                                    justifyContent: "center",
                                    borderRight: "2px solid #E0E0E0",
                                    backgroundColor: !sellList.map(sell => sell.code).includes(params.value as string) ? theme.palette.error.light : undefined
                                })
                            }]
                    },
                ]
            }))
        ];
    };

    const buildMatrixDictionary = (ancillaryDetails: AncillaryDetail[]): MatrixDictionary => {
        return ancillaryDetails.reduce((dictionary: MatrixDictionary, detail) => {
            return {
                ...dictionary,
                [detail.carGroup]: {
                    ...dictionary[detail.carGroup],
                    [detail.prcRentalPeriod.id]: detail
                }
            };
        }, {});
    };

    const insertAncillaryDetailInMatrix = ({
                                               matrixDictionary,
                                               carGroup,
                                               rentalPeriod,
                                               ancillaryDetail
                                           }: { matrixDictionary: MatrixDictionary, carGroup: string, rentalPeriod: RentalPeriodEntityResponse, ancillaryDetail: AncillaryDetail }): MatrixDictionary => {
        const currentRowObject: MatrixElementInterface = matrixDictionary[carGroup];
        if (currentRowObject) matrixDictionary[carGroup] = {
            ...matrixDictionary[carGroup],
            [rentalPeriod.id]: ancillaryDetail
        };
        else matrixDictionary[carGroup] = {
            [rentalPeriod.id]: ancillaryDetail
        };
        return matrixDictionary;
    };

    const buildEmptyAncillaryDetail = ({
                                           ancillary,
                                           carGroup,
                                           rentalPeriod
                                       }: {
        ancillary: AncillaryEntityResponse,
        carGroup: string,
        rentalPeriod: RentalPeriodEntityResponse
    }): AncillaryDetail => {
        return {
            price: watchMatrixInitializationForm.price ? watchMatrixInitializationForm.price.toString().replace(",", ".") : "0",
            sell: watchMatrixInitializationForm.sell?.code as string || watchMatrixUpdateForm.sell?.code as string || "",
            carGroup: carGroup,
            prcAncillary: ancillary,
            prcRentalPeriod: rentalPeriod,
            forfait: false
        };
    };

    const buildMatrix = (ancillaryDetails: AncillaryDetail[], ancillary: AncillaryEntityResponse, rentalPeriods: RentalPeriodEntityResponse[], carGroups: string[]): MatrixDictionary => {
        let matrixDictionary: MatrixDictionary = buildMatrixDictionary(ancillaryDetails);

        // per ciascuna riga, per ciascuna colonna, se esiste un dettaglio ce lo inserisco, se non esiste ne creo uno con valori a 0
        carGroups.forEach((carGroup: string) => {
            rentalPeriods.forEach((rentalPeriod: RentalPeriodEntityResponse) => {
                const firstLevel = matrixDictionary[carGroup];
                let currentDetail: AncillaryDetail | undefined;
                if (firstLevel) currentDetail = firstLevel[rentalPeriod.id];

                // se esiste un dettaglio ce lo inserisco
                if (currentDetail) {
                    matrixDictionary = insertAncillaryDetailInMatrix({
                        matrixDictionary,
                        carGroup,
                        rentalPeriod,
                        ancillaryDetail: currentDetail
                    });
                }
                // se non esiste ne creo uno con valori a 0
                else {
                    const currentEmptyDetail: AncillaryDetail = buildEmptyAncillaryDetail({
                        ancillary,
                        carGroup,
                        rentalPeriod
                    });
                    matrixDictionary = insertAncillaryDetailInMatrix({
                        matrixDictionary,
                        carGroup,
                        rentalPeriod,
                        ancillaryDetail: currentEmptyDetail
                    });
                }
            });
        });

        return matrixDictionary;
    };

    // ************ AG GRID START ************ //
    const columnDefs: ColDef[] = buildMatrixColDef({
        rentalPeriodColumns,
        t,
        ancillary
    });

    const CustomTooltip = (props: ITooltipParams & { color: string }) => {
        const sellType = sellList?.find(sell => sell.code === props.value);
        return (
            <>
                {sellType ? getChip(sellType) : null}
            </>
        );
    };

    const defaultColDef = {
        ...DEFAULT_MATRIX_COL_DEF,
        wrapHeaderText: true,
        tooltipComponent: CustomTooltip
    };
    // ************ AG GRID - END ************ //

    const checkMatrix = (): AncillaryDetail[] | undefined => {
        const rowsFromAgGrid: MatrixElementInterface[] = getRowsFromAgGrid();
        let rentalPeriodsConsecutive = false;
        let emptyMatrix = false;
        let sellIsEmpty = false;
        let wrongSellType = false;

        try {
            let rentalPeriodsAreConsecutive: boolean = true;
            const rentalPeriodColumsFiltered = rentalPeriodColumns.filter(rp => rp.weekend === false)

            rentalPeriodColumsFiltered.forEach((rp, index) => {
                if (rentalPeriodsAreConsecutive && index < rentalPeriodColumsFiltered.length - 1) {
                    rentalPeriodsAreConsecutive = ((rentalPeriodColumsFiltered[index + 1].from - rp.to) === 1);
                }
            });
            if (!rentalPeriodsAreConsecutive) {
                rentalPeriodsConsecutive = true;
                throw t("pricing:ancillaryMatrix.errors.rentalPeriodsConsecutive");
            }

            const persistArray: AncillaryDetail[] = [];

            for (const matrixElementInterface of rowsFromAgGrid) {
                Object.values(matrixElementInterface).forEach((ancillaryDetail: AncillaryDetail) => {
                    if (ancillaryDetail.prcAncillary !== null && ancillaryDetail.prcAncillary !== undefined && ancillaryDetail.prcAncillary.taxCode !== null && ancillaryDetail.prcAncillary.taxCode !== undefined) {
                        const taxCodeObject = ancillaryDetail.prcAncillary.taxCode as unknown as TaxCodeObject;
                        ancillaryDetail.prcAncillary = {
                            ...ancillaryDetail.prcAncillary,
                            taxCode: taxCodeObject.value
                        };
                    }

                    persistArray.push({
                        ...ancillaryDetail,
                        price: convertToCents(ancillaryDetail.price)
                    });
                });
            }

            const ancillaryDetailWithNumericSell = persistArray.find(ancillaryDetail => {
                if (!sellList.map(sell => sell.code)
                    .includes(ancillaryDetail.sell as string)
                ) {
                    return ancillaryDetail;
                }
            });

            if (persistArray.find(el => el.sell === "")) {
                sellIsEmpty = true;
                throw t("pricing:ancillaryMatrix.errors.emptySell");
            } else if (ancillaryDetailWithNumericSell) {
                wrongSellType = true;
                throw t("pricing:ancillaryMatrix.errors.wrongSellType");
            }

            // NON PROSEGUIRE SE MATRICE VUOTA
            if (persistArray.length === 0) {
                emptyMatrix = true;
                throw t("pricing:ancillaryMatrix.errors.emptyMatrix");
            } else {
                if (ancillary) {
                    return persistArray;
                }
            }
        } catch (e) {
            console.log(e);
            if (rentalPeriodsConsecutive) dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("pricing:ancillaryMatrix.errors.rentalPeriodsConsecutive"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
            else if (sellIsEmpty) dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("pricing:ancillaryMatrix.errors.emptySell"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
            else if (wrongSellType) dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("pricing:ancillaryMatrix.errors.wrongSellType"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
            else if (emptyMatrix) dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("pricing:ancillaryMatrix.errors.emptyMatrix"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        }
    };

    const handlePersistMatrix = async (persistArray: AncillaryDetail[]) => {
        try {
            if (ancillary && persistArray) {
                const ancillaryUpdated: AncillaryEntityResponse = {...ancillary, used: ancillaryInUse};

                const taxCodeObject = ancillaryUpdated.taxCode as unknown as TaxCodeObject;
                ancillaryUpdated.taxCode = serviceItem.find(
                    (item: any) => item.name === ancillaryUpdated.taxCode
                )?.code || taxCodeObject.value;

                await persistAncillaryAndDetails({
                    prcAncillary: ancillaryUpdated,
                    ancillaryDetails: persistArray
                });

                setDialog(null);
                dispatchGlobalData(SET_SNACKBAR.SUCCESS);

                navigate("../ancillary/ancillary");
            }
        } catch (e) {
        }
    };

    const handleDeleteMatrix = async () => {
        try {
            setDialog(null);
            setAncillaryDetails([]);
            setMatrix({});
            setCarGroupRows(allCarGroupRows);
            setRentalPeriodColumns(allRentalPeriodColumns);
            setInitialMatrix({});
        } catch (err) {
        }
    };

    const setGridRefColumDefsAndRowData = ({
                                               rentalPeriods,
                                               carGroups,
                                               matrix
                                           }: {
        rentalPeriods: RentalPeriodEntityResponse[]
        carGroups: string[]
        matrix: MatrixDictionary
    }) => {
        const columnDefs: ColDef[] = buildMatrixColDef({
            rentalPeriodColumns: rentalPeriods,
            t,
            ancillary
        });

        gridRef?.current?.api?.setColumnDefs(columnDefs);
        gridRef?.current?.api?.setRowData(Object.entries(matrix).map(([key, value]) => {
            return {
                carGroup: key,
                ...value
            };
        }));
        gridRef?.current?.api?.refreshHeader();
        gridRef?.current?.api?.refreshCells();

        setMatrix(matrix);

        setCarGroupRows(carGroups.length === 0 ? allCarGroupRows : carGroups);
        setRentalPeriodColumns(rentalPeriods.length === 0 ? allRentalPeriodColumns : rentalPeriods);
    };

    const handleCancel = () => {
        setDialog(null);
        const accumulator = JSON.parse(JSON.stringify({...initialMatrix}));

        setGridRefColumDefsAndRowData({
            rentalPeriods: initialRentalPeriodColumns,
            carGroups: initialCarGroupRows,
            matrix: accumulator
        });
        navigate("../ancillary/ancillary");
    };

    // ************ FORM INITIALIZE MATRIX- START ************ //
    const submitInitialization = async (values: MatrixInitializationValues) => {
        let rentalPeriodsConsecutive = false;
        //let multipleDiscountsFound = false;
        try {
            // create empty matrix
            if (values.matrixInitializationType === 0 && values.rentalPeriods && values.carGroups && ancillary) {
                const rentalPeriodsSubGroup: SelectElementModelFE[] = values.rentalPeriods.filter(el => el.code !== 0);
                const carGroupsSubGroup: string[] = values.carGroups.filter(el => el !== t("pricing:actions.selectAll"));

                const actualRentalPeriods: RentalPeriodEntityResponse[] = rentalPeriodColumns.filter((rp: RentalPeriodEntityResponse) => {
                    return rentalPeriodsSubGroup.find((rpSub: SelectElementModelFE) => rpSub.code === rp.id);
                });

                const actualRentalPeriodsFiltered = actualRentalPeriods.filter(rp => rp.weekend === false)

                let rentalPeriodsAreConsecutive: boolean = true;
                actualRentalPeriodsFiltered.forEach((rp, index) => {
                    if (rentalPeriodsAreConsecutive && index < actualRentalPeriodsFiltered.length - 1) {
                        rentalPeriodsAreConsecutive = ((actualRentalPeriodsFiltered[index + 1].from - rp.to) === 1);
                    }
                });
                if (!rentalPeriodsAreConsecutive) {
                    rentalPeriodsConsecutive = true;
                    throw t("pricing:ancillaryMatrix.errors.rentalPeriodsConsecutive");
                }

                const actualCarGroups: string[] = carGroupRows.filter((cg: string) => {
                    return carGroupsSubGroup.find((cgSub: string) => cgSub === cg);
                });

                const rentalPeriodSorted: RentalPeriodEntityResponse[] = await getRentalPeriodsSorted(actualRentalPeriods)

                //costruire le righe della matrice a partire dal car group e mettendo in fila i periodi di noleggio
                const accumulator: MatrixDictionary = buildMatrix([], ancillary, rentalPeriodSorted, actualCarGroups);

                setRentalPeriodColumns(rentalPeriodSorted);
                setCarGroupRows(actualCarGroups);

                matrixUpdateForm.setValue("rentalPeriods", rentalPeriodSorted.map(rp => ({
                    code: rp.id,
                    label: rp.name
                })));
                matrixUpdateForm.setValue("carGroups", actualCarGroups);

                setMatrix(accumulator);

                setInitialMatrix(JSON.parse(JSON.stringify(accumulator)));
            }
            // copy matrix
            else if (values.matrixInitializationType === 1 && values.ancillary && ancillary) {
                // if (values.discountIncreaseFixed !== null && values.discountIncreasePercentage !== null) {
                //     multipleDiscountsFound = true;
                //     throw t("pricing:ancillaryMatrix.errors.multipleDiscountsFound");
                // }

                const ancillaryDetails = mapAncillaryDetails({
                    ancillaryDetails: await getAncillaryDetailsFromCopyAncillary({
                        originalAncillaryId: values.ancillary.code as number,
                        destinationAncillaryId: ancillary.id || values.ancillary.code as number,
                        fixedDiscount: values.discountIncreaseFixed ? convertToCents(values.discountIncreaseFixed) : null,
                        percentDiscount: values.discountIncreasePercentage ? Number(values.discountIncreasePercentage) : null
                    })
                });

                const carGroupsAndRentalPeriods = populateCarGroupsAndRentalPeriods({ancillaryDetails});
                const rentalPeriods = carGroupsAndRentalPeriods.rentalPeriods;
                const rentalPeriodSorted: RentalPeriodEntityResponse[] = await getRentalPeriodsSorted(rentalPeriods)
                const carGroups = carGroupsAndRentalPeriods.carGroups;

                setCarGroupsAndRentalPeriodsAndMatrix({
                    rentalPeriods: rentalPeriodSorted,
                    carGroups,
                    allRentalPeriods: allRentalPeriodColumns,
                    allCarGroups: allCarGroupRows,
                    ancillary,
                    ancillaryDetails,
                    setRentalPeriodColumns,
                    setAllRentalPeriodColumns,
                    setInitialRentalPeriodColumns,
                    setCarGroupRows,
                    setAllCarGroupRows,
                    setInitialCarGroupRows,
                    setMatrix,
                    setInitialMatrix,
                    buildMatrix
                });
            }
            matrixInitializationForm.reset();
        } catch (e) {
            if (rentalPeriodsConsecutive) dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("pricing:ancillaryMatrix.errors.rentalPeriodsConsecutive"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
            // if (multipleDiscountsFound) dispatchGlobalData({
            //     type: setSnackbar,
            //     payload: {
            //         snackbar: {
            //             message: t("pricing:ancillaryMatrix.errors.multipleDiscountsFounds"),
            //             type: snackbarTypes.error,
            //             show: true,
            //             autoClose: true
            //         }
            //     }
            // });
        }
    };

    const handleSubmitInitialization = (values: MatrixInitializationValues) => {
        submitInitialization(values);
    };

    const matrixInitializationForm = useForm<MatrixInitializationValues>({
        defaultValues: initialValues,
        resolver: yupResolver(
            validationSchema({t}),
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: true
    });
    const watchMatrixInitializationForm = matrixInitializationForm.watch();
    // ************ FORM INITIALIZE MATRIX - END ************ //

    // ************ FORM UPDATE MATRIX - START ************ //
    const updateValues: MatrixInitializationValues = {
        matrixInitializationType: 0,
        carGroups: carGroupRows,
        rentalPeriods: fromGenericBeToGenericFe(rentalPeriodColumns),
        ancillary: null,
        discountIncreasePercentage: null,
        discountIncreaseFixed: null
    };

    const matrixUpdateForm = useForm<MatrixInitializationValues>({
        defaultValues: updateValues,
        resolver: yupResolver(
            validationSchema({t}),
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: true
    });
    const watchMatrixUpdateForm = matrixUpdateForm.watch();
    // ************ FORM UPDATE MATRIX - END ************ //

    const getRowsFromAgGrid = () => {
        const rowsFromAgGrid: MatrixElementInterface[] = [];
        if (gridApi) gridApi.forEachNodeAfterFilterAndSort((node: any) => {
            const newNodeData = Object.entries(node.data)
                .filter(([key]: [string, any]) => key !== "carGroup")
                .reduce((acc: any, [key, value]: [string, any]) => {
                    return {
                        ...acc,
                        [key]: {...value}
                    };
                }, {});
            rowsFromAgGrid.push(newNodeData);
        });
        return rowsFromAgGrid;
    };

    const handleChangeAgGridStructure = ({
                                             rentalPeriods,
                                             carGroups
                                         }: {
        rentalPeriods: RentalPeriodEntityResponse[]
        carGroups: string[]
    }) => {
        const rowsFromAgGrid: MatrixElementInterface[] = getRowsFromAgGrid();

        const persistArray: AncillaryDetail[] = [];

        for (const matrixElementInterface of rowsFromAgGrid) {
            Object.values(matrixElementInterface).forEach((ancillaryDetail: AncillaryDetail) => {
                if (
                    rentalPeriods.find(rp => rp.id === ancillaryDetail.prcRentalPeriod.id)
                    &&
                    carGroups.find(cg => ancillaryDetail.carGroup === cg)
                ) persistArray.push({...ancillaryDetail});
            });
        }

        if (ancillary) {
            const accumulator: MatrixDictionary = buildMatrix(persistArray, ancillary, rentalPeriods, carGroups);

            setGridRefColumDefsAndRowData({
                rentalPeriods,
                carGroups,
                matrix: accumulator
            });
        }
    };

    const handleChangeCarGroups = (newCarGroups: string[]) => {
        if (ancillary) {
            changeCarGroups({
                t,
                newCarGroups,
                handleChangeAgGridStructure,
                allRentalPeriodColumns,
                rentalPeriodColumns
            });
        }
    };

    const handleChangeRentalPeriods = (newRentalPeriods: SelectElementModelFE[]) => {
        if (ancillary) {
            changeRentalPeriods({
                newRentalPeriods,
                allRentalPeriodColumns,
                handleChangeAgGridStructure,
                initialCarGroupRows,
                carGroupRows,
                getRentalPeriodsSorted
            });
        }
    };

    const getChip = (s: SelectElementModelFE) => {
        let color;
        switch (s.code) {
            case("A"): {
                color = "#BEE5CA";
                break;
            }
            case("N"): {
                color = theme.palette.neutral.light;
                break;
            }
            case("Z"): {
                color = "#99D9F2";
                break;
            }
            case("S"): {
                color = "#FBEC9B";
                break;
            }
            case("B"): {
                color = "#E999CB";
                break;
            }
            case("R"): {
                color = "#F39D9D";
                break;
            }
            default: {
                break;
            }
        }
        return <Chip label={`${s.code} - ${s.label}`}
                     sx={{bgcolor: color}}
                     variant={"filled"}/>;
    };

    const getLabelChip = (sellType: string) => {
        let color;
        switch (sellType) {
            case("A"): {
                color = "#BEE5CA";
                break;
            }
            case("N"): {
                color = theme.palette.neutral.light;
                break;
            }
            case("Z"): {
                color = "#99D9F2";
                break;
            }
            case("S"): {
                color = "#FBEC9B";
                break;
            }
            case("B"): {
                color = "#E999CB";
                break;
            }
            case("R"): {
                color = "#F39D9D";
                break;
            }
            default: {
                break;
            }
        }
        return <Chip label={`${sellType}`}
                     sx={{bgcolor: color, px: 2}}
                     variant={"filled"}/>;
    };

    function processCellFromClipboard(params: ProcessCellForExportParams) {
        // @ts-ignore
        if (!isNaN(params.value.replace(",", ".")) && params.column.colId.includes("price")) {
            return params.value;
            // @ts-ignore
        } else if (params.column.colId.includes("sell") && sellList.map(sell => sell.code).includes(params.value)) {
            return params.value;
        } else {
            return "";
        }
    }

    const handleChangeSellType = useCallback((value: SelectElementModelFE | null) => {
        matrixUpdateForm.setValue("sell", value);
        if (value) {
            gridRef.current!.api.forEachNode((node) => {
                const updatedNodeData: any = {};
                Object.entries(node.data).forEach(([key, content]: [key: string, content: any], index) => {
                    if (key !== "carGroup") {
                        content.sell = value.code;
                        if (content.sell === "Z") {
                            content.price = 0
                        }
                    }
                    updatedNodeData[key] = content;
                });
                node.updateData(updatedNodeData);
            });
        }
    }, []);

    return (
        <Grid container spacing={2} alignItems="start">
            {!readOnly && ancillaryInUse &&
                <CustomBanner message={t("pricing:ancillaryMatrix.banner")} severity={"warning"}/>}
            {loading ?
                null
                :
                Object.entries(matrix).length > 0 ?
                    <Grid item xs={12}>
                        {/* UPDATE MATRICE */}
                        {!readOnly &&
                            // modificare righe e colonne della matrice solo se NON import da excel (matrixInitializationType !== 2)
                            matrixInitializationForm.getValues().matrixInitializationType !== 2 &&
                            <FormProvider {...matrixUpdateForm}>
                                <Grid container spacing={2} pb={2} item xs={12}>
                                    <CarGroupsAndRentalPeriods carGroups={allCarGroupRows}
                                                               rentalPeriods={allRentalPeriodColumns}
                                                               handleChangeCarGroups={handleChangeCarGroups}
                                                               handleChangeRentalPeriods={handleChangeRentalPeriods}
                                    />
                                    <Grid item xs={12} md={3}>
                                        <FormAutocomplete
                                            {...matrixUpdateForm.register("sell")}
                                            label={`${t("pricing:ancillaryMatrixInsert.form.labels.sell")}*`}
                                            options={sellList}
                                            handleChange={handleChangeSellType}
                                            isOptionEqualToValue={(option, value) => option.code === value.code}
                                        />
                                    </Grid>
                                </Grid>
                            </FormProvider>
                        }
                        {/* MATRICE DEI PREZZI */}
                        <AgGridCustomTable
                            gridRef={gridRef}
                            defaultColDef={defaultColDef}
                            columnDefs={columnDefs}
                            height={heightDynamicMatrix(carGroupRows as string[], 145)}
                            frameworkComponents={{
                                customEditor: ancillary?.chargeType === "VALUE" ? NumberCustomEditorComponent : PercentageCellEditor
                            }}
                            pagination={false}
                            localeText={agGridLocale}
                            tooltipShowDelay={0}
                            stopEditingWhenCellsLoseFocus
                            rowData={Object.entries(matrix).map(([key, value]) => {
                                return {
                                    carGroup: key,
                                    ...value
                                };
                            })}
                            rowSelection={"multiple"}
                            suppressRowClickSelection={true}
                            enableRangeSelection={true}
                            enableCellTextSelection={false}
                            onGridReady={onGridReady}
                            processCellFromClipboard={processCellFromClipboard}
                        />
                        {/* sell types legend */}
                        <Grid container item xs={12} columnGap={2} my={2}>
                            {t("pricing:ancillaryMatrix.labels.sellType")}*:{sellList.map((s: SelectElementModelFE) => getChip(s))}
                        </Grid>
                        {!readOnly &&
                            <Grid container item xs={12} justifyContent="space-between" columnGap={2} my={2}>
                                <Grid>
                                    {ancillaryDetails?.length > 0 &&
                                        <Button color={"accent"}
                                                type={"button"}
                                                onClick={handleDeleteMatrix}
                                        >
                                            {t("pricing:actions.delete")}
                                        </Button>
                                    }
                                </Grid>
                                <Grid>
                                    <Button variant="outlined"
                                            sx={{marginRight: "15px"}}
                                            color={"accent"}
                                            type={"button"}
                                            onClick={() => setDialog({
                                                open: true,
                                                message: t("pricing:dialogs.messages.looseChanging"),
                                                onClickConfirm: () => {
                                                    window.close()
                                                }
                                            })}
                                    >
                                        {t("pricing:actions.back")}
                                    </Button>
                                    <Button color={"accent"}
                                            type={"button"}
                                            onClick={() => {
                                                const persistArray = checkMatrix();
                                                if (persistArray) {
                                                    setDialog({
                                                        open: true,
                                                        message: ancillaryId ?
                                                            t("pricing:ancillaryMatrix.banner")
                                                            :
                                                            t("pricing:ancillaryMatrix.dialogContents.createAncillaryMatrix"),
                                                        onClickConfirm: () => handlePersistMatrix(persistArray)
                                                    });
                                                }
                                            }}
                                    >
                                        {t("pricing:actions.confirm")}
                                    </Button>
                                </Grid>
                            </Grid>
                        }
                    </Grid>
                    :
                    readOnly ?
                        ancillary?.isOneWay ?
                            null
                            :
                            // NO MATRICE DEI PREZZI
                            <Grid item xs={12} justifyContent={"center"} p={2}>
                                {t("pricing:ancillaryMatrixInsert.noMatrix")}
                            </Grid>
                        :
                        // INITIALIZE DEI PREZZI
                        <Grid item xs={12}>
                            <FormProvider {...matrixInitializationForm}>
                                <MatrixInitialization carGroups={carGroupRows}
                                                      chargeType={ancillary?.chargeType}
                                                      rentalPeriods={rentalPeriodColumns}
                                                      sellList={sellList}
                                                      handleCancel={() => setDialog({
                                                          open: true,
                                                          message: t("pricing:dialogs.messages.looseChanging"),
                                                          onClickConfirm: () => {
                                                              setDialog(null);
                                                              setValue(0);
                                                          }
                                                      })}
                                                      handleSubmit={matrixInitializationForm.handleSubmit(handleSubmitInitialization)}
                                />
                            </FormProvider>
                        </Grid>
            }
            {dialog?.open &&
                <DialogComponent
                    type={"info"}
                    onClickConfirm={dialog.onClickConfirm}
                    setOpen={() => setDialog(null)}
                    open={dialog.open}
                    message={dialog.message}
                />
            }
        </Grid>
    );
};

export default AncillaryMatrix;
